// The map's server data: the assembled world blob (the Worker reads the game's chunk files and caches them), one
// village/info per tapped city, bookmarks, the guild rankings (names for the legend) and guild details (tags).
//
// The world blob (Oct 14 2026): kept on the device after the first load (src/map/mapStore.ts) and shown from there at
// once; then checked every five minutes (and on coming back to the tab once that long has passed) with the copy's
// version tag — the Worker answers 304 when nothing changed, so an unchanged map costs no download and no redraw.
import { useEffect, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { BookmarksResult } from "@/api/types";
import { GamePromptError, signedOutByServer } from "@/api/http";
import { parseWorldMap, type WorldMap } from "@/map/chunks";
import { readSavedMap, saveMap, touchSavedMap } from "@/map/mapStore";

/** How often an open map asks whether the world changed. */
export const MAP_POLL = 5 * 60_000;

export const mapKeys = {
  world: (wid: number, size: number) => ["worldmap", wid, size] as const,
  info: (wid: number, x: number, y: number) => ["village-info", wid, x, y] as const,
  bookmarks: (wid: number) => ["bookmarks", wid] as const,
  guilds: (wid: number) => ["guild-rankings", wid] as const,
  guild: (wid: number, gid: number) => ["guild-details", wid, gid] as const,
  player: (wid: number, uid: number) => ["player-info", wid, uid] as const,
};

/** The version tag of the copy each world's query holds ("wid:size" → ETag). */
const etags = new Map<string, string>();
/** The look for each world's kept copy, once a session ("wid:size" → done). */
const looked = new Map<string, Promise<void>>();

async function fetchWorldMap(qc: QueryClient, wid: number, size: number): Promise<WorldMap> {
  const k = `${wid}:${size}`;
  const have = qc.getQueryData<WorldMap>(mapKeys.world(wid, size));
  const tag = have ? etags.get(k) : undefined;
  let res: Response;
  try {
    // a conditional request skips the browser's own HTTP cache (fetch makes it no-store) and comes back 304 as is
    const headers: Record<string, string> = { accept: "application/octet-stream" };
    if (tag) headers["if-none-match"] = tag;
    res = await fetch(`/api/map/${wid}?size=${size}`, { credentials: "same-origin", headers });
  } catch {
    throw new GamePromptError("No connection. Check your network and try again.", [], "net");
  }
  if (res.status === 304 && have) {
    void touchSavedMap(wid, size);
    return have; // the same object: nothing downstream redraws
  }
  if (res.status === 401) {
    signedOutByServer();
    throw new GamePromptError("You're signed out.", [], "signed_out");
  }
  if (!res.ok) throw new GamePromptError(res.status === 502 ? "The game's map files didn't load. Try again in a moment." : `The map didn't load (${res.status}).`, [], "http");
  let buf = await res.arrayBuffer();
  // still gzipped (a proxy or a test harness passed the body through untouched): unpack it here
  const b = new Uint8Array(buf);
  if (b.length > 2 && b[0] === 0x1f && b[1] === 0x8b && typeof DecompressionStream !== "undefined") {
    buf = await new Response(new Blob([buf]).stream().pipeThrough(new DecompressionStream("gzip"))).arrayBuffer();
  }
  const map = parseWorldMap(buf);
  const etag = res.headers.get("etag");
  if (etag) etags.set(k, etag);
  else etags.delete(k);
  void saveMap(wid, size, buf, etag);
  return map;
}

/** Put the device's kept copy of this world's map into the query (once a session), dated when the server last
 *  confirmed it — so the map draws at once, and a copy older than MAP_POLL is checked straight away. */
function seedFromDevice(qc: QueryClient, wid: number, size: number): Promise<void> {
  const k = `${wid}:${size}`;
  let p = looked.get(k);
  if (!p) looked.set(k, (p = seed(qc, wid, size, k).catch(() => undefined)));
  return p;
}

async function seed(qc: QueryClient, wid: number, size: number, k: string): Promise<void> {
  const key = mapKeys.world(wid, size);
  if (qc.getQueryData(key)) return;
  const saved = await readSavedMap(wid, size);
  if (!saved || qc.getQueryData(key)) return;
  let map: WorldMap;
  try {
    map = parseWorldMap(saved.buf);
  } catch {
    return; // an unreadable copy: download afresh
  }
  if (saved.meta.etag) etags.set(k, saved.meta.etag);
  qc.setQueryData(key, map, { updatedAt: Math.min(saved.meta.checkedAt, Date.now()) });
}

/** The whole world's cities: the device's copy at once, then checked for changes every five minutes while the map is open. */
export function useWorldMap(wid: number | null, size: number | undefined) {
  const qc = useQueryClient();
  const k = `${wid ?? 0}:${size ?? 0}`;
  const [ready, setReady] = useState<string | null>(null);
  useEffect(() => {
    if (!wid || !size) return;
    let live = true;
    void seedFromDevice(qc, wid, size).finally(() => { if (live) setReady(k); });
    return () => { live = false; };
  }, [qc, wid, size, k]);
  return useQuery({
    queryKey: mapKeys.world(wid ?? 0, size ?? 0),
    queryFn: () => fetchWorldMap(qc, wid!, size!),
    // wait for the look at the device's copy (a few ms) so it isn't raced by a full download
    enabled: !!wid && !!size && ready === k,
    staleTime: MAP_POLL - 5_000,
    refetchInterval: MAP_POLL,
    refetchIntervalInBackground: false,
    gcTime: 60 * 60_000,
  });
}

/** village/info for a tapped cell (cached ten minutes). */
export function useVillageInfo(wid: number, cell: [number, number] | null) {
  return useQuery({
    queryKey: mapKeys.info(wid, cell?.[0] ?? -1, cell?.[1] ?? -1),
    queryFn: () => api.villageInfo({ wid }, cell![0], cell![1]),
    enabled: !!cell,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export const bookmarkList = (arr: Record<string, string>[] | undefined) => (arr ?? []).flatMap((o) => Object.entries(o).map(([name, coords]) => ({ name, coords })));

export function useBookmarks(wid: number, enabled = true) {
  return useQuery({ queryKey: mapKeys.bookmarks(wid), queryFn: () => api.bookmarks.list({ wid }), enabled, staleTime: 60_000 });
}

export function useBookmarkMutations(wid: number) {
  const qc = useQueryClient();
  const put = (r: BookmarksResult) => qc.setQueryData(mapKeys.bookmarks(wid), r);
  const add = useMutation({ mutationFn: (coords: string) => api.bookmarks.add({ wid }, [coords]), onSuccess: put });
  const remove = useMutation({ mutationFn: (coords: string) => api.bookmarks.delete({ wid }, [coords]), onSuccess: put });
  /** several at once (the Bookmarks sheet's Edit › Delete): the game's delete takes a list */
  const removeMany = useMutation({ mutationFn: (coords: string[]) => api.bookmarks.delete({ wid }, coords), onSuccess: put });
  return { add, remove, removeMany };
}

export interface GuildBrief {
  gid: number;
  name: string;
  tag?: string;
  cities: number;
  rank: number;
  score: number;
}

/** Every guild in the world from the guild board — rankings/player with lb_class "guild", 20 a page — by id: names for
 *  the realm legend and the sheets. (rankings/guild is something else: YOUR guild's member ranking.) A player in no
 *  guild gets an empty board from the server; the legend then falls back to guild/details per tag. */
export function useGuildRankings(wid: number, enabled = true) {
  return useQuery({
    queryKey: mapKeys.guilds(wid),
    queryFn: async () => {
      const out = new Map<number, GuildBrief>();
      const page = (p: number) => api.rankings.player({ wid }, { lb_class: "guild", lb_info: "score", page_num: p });
      const first = await page(1);
      const pages = Math.min(first.output?.total_pages ?? 1, 25);
      const take = (r: typeof first) => {
        for (const g of r.output?.results ?? []) {
          const gid = Number(g.member);
          out.set(gid, { gid, name: g.user_info?.gname ?? `Guild ${gid}`, cities: g.user_info?.num_villages ?? 0, rank: g.rank, score: g.score });
        }
      };
      take(first);
      for (let p = 2; p <= pages; p++) take(await page(p));
      return out;
    },
    enabled,
    staleTime: 10 * 60_000,
  });
}

export function useGuildDetails(wid: number, vid: number, gid: number | null) {
  return useQuery({
    queryKey: mapKeys.guild(wid, gid ?? 0),
    queryFn: () => api.guild.details({ wid, vid }, gid!),
    enabled: !!gid && gid > 0 && !!vid,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function usePlayerInfo(wid: number, uid: number | null) {
  return useQuery({
    queryKey: mapKeys.player(wid, uid ?? 0),
    queryFn: () => api.player.info({ wid }, uid!),
    enabled: !!uid && uid > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** The tags of a few guilds (the legend's), from guild/details, cached. */
export function useGuildTags(wid: number, vid: number | null, gids: number[]): Map<number, string> {
  const results = useQueries({
    queries: gids.map((gid) => ({
      queryKey: mapKeys.guild(wid, gid),
      queryFn: () => api.guild.details({ wid, vid: vid! }, gid),
      enabled: !!vid && gid > 0,
      staleTime: 10 * 60_000,
      retry: false,
    })),
  });
  const m = new Map<number, string>();
  results.forEach((r, i) => { const t = r.data?.guild_info?.tag; if (t) m.set(gids[i], t); });
  return m;
}
